<template>
  <div class="page-main-container" ref="containerRef" @scroll="onContainerScroll">
    <div class="page-main-slider" :style="sliderStyle">
      <!-- Panel 1: Recipes (Left Tab, 0%) -->
      <div class="slider-panel" :inert="activeTab !== 'recipes'">
        <PageRecipes />
      </div>

      <!-- Panel 2: Shopping List (Right Tab, -50%) -->
      <div class="slider-panel" :inert="activeTab !== 'shopping-list'">
        <PageShoppingList />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, defineAsyncComponent, onMounted, onUnmounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useSwipeDrawer } from '../composables/useSwipeDrawer'
import { useAppStore } from '../stores/appStore'
import { hasActiveModal } from '../composables/useModalBackHandler'
const PageRecipes = defineAsyncComponent(() => import('./PageRecipes.vue'))
const PageShoppingList = defineAsyncComponent(() => import('./PageShoppingList.vue'))

const route = useRoute()
const router = useRouter()
const store = useAppStore()
const containerRef = ref<HTMLElement | null>(null)

const onContainerScroll = () => {
  if (containerRef.value && containerRef.value.scrollLeft !== 0) {
    containerRef.value.scrollLeft = 0
  }
}

const { isDragging, isSidebarOpen, gestureType, pendingMainTab, mainTabProgress } = useSwipeDrawer()

const activeTab = computed(() => {
  if (pendingMainTab.value) return pendingMainTab.value
  if (mainTabProgress.value !== null) {
    return mainTabProgress.value >= 0.5 ? 'shopping-list' : 'recipes'
  }
  return route.path === '/recipes' ? 'recipes' : 'shopping-list'
})

const onKeyDown = (e: KeyboardEvent) => {
  if (e.repeat) return
  if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return
  if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return

  // Shield inputs, textareas, selects, and contenteditable elements
  const activeEl = document.activeElement
  if (activeEl) {
    const tagName = activeEl.tagName.toLowerCase()
    if (
      tagName === 'input' || 
      tagName === 'textarea' || 
      tagName === 'select' || 
      (activeEl as HTMLElement).isContentEditable
    ) {
      return
    }
  }

  // Shield if dragging, any modal, global search, notification overlay, or sidebar is open
  if (
    isDragging.value ||
    hasActiveModal() || 
    store.isGlobalSearchOpen || 
    store.isNotificationsModalOpen || 
    isSidebarOpen.value
  ) {
    return
  }

  if (e.key === 'ArrowRight' && activeTab.value === 'recipes') {
    e.preventDefault()
    const wasTabFocused = !!activeEl?.closest('.header-tab-bar')
    router.replace('/shopping-list')
    if (wasTabFocused) {
      nextTick(() => {
        const tabEl = document.querySelector<HTMLElement>('.header-tab-bar a[href$="/shopping-list"]')
        tabEl?.focus()
      })
    }
  } else if (e.key === 'ArrowLeft' && activeTab.value === 'shopping-list') {
    e.preventDefault()
    const wasTabFocused = !!activeEl?.closest('.header-tab-bar')
    router.replace('/recipes')
    if (wasTabFocused) {
      nextTick(() => {
        const tabEl = document.querySelector<HTMLElement>('.header-tab-bar a[href$="/recipes"]')
        tabEl?.focus()
      })
    }
  }
}

onMounted(() => {
  window.addEventListener('keydown', onKeyDown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', onKeyDown)
})

const sliderStyle = computed(() => {
  if (isDragging.value && gestureType.value === 'page-switch' && mainTabProgress.value !== null) {
    const totalTranslate = Math.max(-50, Math.min(0, -mainTabProgress.value * 50))
    return {
      transform: `translate3d(${totalTranslate}%, 0, 0)`,
      transition: 'none'
    }
  }

  const targetBase = activeTab.value === 'recipes' ? 0 : -50
  return {
    transform: `translate3d(${targetBase}%, 0, 0)`,
    transition: 'transform 0.32s cubic-bezier(0.25, 1, 0.5, 1)'
  }
})
</script>

<style scoped>
.page-main-container {
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  padding-top: var(--page-content-padding-top, 56px);
  box-sizing: border-box;
}

:global(.has-top-banner) .page-main-container {
  padding-top: 0 !important;
}

.page-main-slider {
  display: flex;
  flex-direction: row;
  width: 200%;
  height: 100%;
  will-change: transform;
}

.slider-panel {
  width: 50%;
  height: 100%;
  flex-shrink: 0;
  overflow: hidden;
  position: relative;
}

.panel-placeholder {
  width: 100%;
  height: 100%;
  background-color: var(--bg-primary);
}
</style>
